import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import {
  WEB_COMPONENT_XDR_SEARCH_QUERY_PANEL,
  WEB_COMPONENT_XDR_SEARCH_RESULTS_PANEL,
  WEB_COMPONENT_GRID_SMART_VIEWS,
  WEB_COMPONENT_GZ_PUSH_BODY_LAYOUT,
} from '../../../webcomponents.constants';
import { Target } from '@nirvana/shared/dtos/types/dashboards/common';
import { SectionType, SmartViewInlineActions, SmartViewSection, ViewType } from '../../../grid/gridSmartViews/types';
import { getRequestDefaultOptions } from '../../../utils/request.utils';
import { HELPER_LINKS } from '../types';
import { htmlDecode } from '../../../utils/escape.utils';
import { ConsoleRequest, DataProviderType } from '../../../utils/request';

export class XdrSearchView extends BaseWebComponentView {
  getDomElements() {
    this.dom.$detailsPanel = this.shadow.querySelector('gz-side-menu');
    this.dom.$searchQueryPanel = this.shadow.querySelector(WEB_COMPONENT_XDR_SEARCH_QUERY_PANEL);
    this.dom.$searchResultsPanel = this.shadow.querySelector(WEB_COMPONENT_XDR_SEARCH_RESULTS_PANEL);
    this.dom.$pushBodyLayout = this.shadow.querySelector(WEB_COMPONENT_GZ_PUSH_BODY_LAYOUT);
    this.dom.$pushBodyLayout.isOpen = true;
    this.dom.$smartViews = this.dom.$pushBodyLayout.querySelector(WEB_COMPONENT_GRID_SMART_VIEWS);
    this.dom.$smartViewsTitle = this.shadow.querySelector('#smart-views-title');

    this.getTranslations();
    this.setTranslatedStrings();
    this.injectDependenciesInChildren();
  }

  getTranslations() {
    this.translations = this.stringsTranslator.getXdrSearchStrings();
    this.translations.inlineActionsTranslations = this.stringsTranslator.getInlineActionsCommonStrings();
    this.commonStrings = this.stringsTranslator.getCommonStrings();
  }

  setTranslatedStrings() {
    this.dom.$smartViewsTitle.innerHTML = this.dependencies.translateFn('Smart views');
  }

  private injectDependenciesInChildren() {
    if (this.dom.$searchResultsPanel.injectDependencies) {
      this.dom.$searchResultsPanel.injectDependencies(this.dependencies);
      this.dom.$searchResultsPanel.start().catch(console.error);
    }
  }

  async buildView() {
    this.displaySearchQueryPanel();
    await this.loadSmartViewItems();
  }

  async onCompanyClicked(clickedCompany: Target) {
    this.dependencies.setActiveCompany(clickedCompany.companyId, clickedCompany.companyName);
  }

  private async displaySearchQueryPanel() {
    const {
      invalidQuery,
      invalidField,
      invalidOperator,
      missingValue,
      invalidValue,
      missingOperator,
      titleQueryPanel,
      queryBtnString,
      clearBtnString,
      hintTextString,
      queryInputPlaceholder,
      datetimeFilterLabel,
    } = this.translations;

    const errorMessages = {
      invalid_query: invalidQuery,
      invalid_field: invalidField,
      invalid_operator: invalidOperator,
      missing_value: missingValue,
      invalid_value: invalidValue,
      missing_operator: missingOperator,
    };

    this.dom.$searchQueryPanel.setAttribute('pageTitle', titleQueryPanel);
    this.dom.$searchQueryPanel.setAttribute('istab', this.shadow.host.hasAttribute('istab'));
    this.dom.$searchQueryPanel.searchQueryProps = {
      queryBtnString: queryBtnString,
      clearBtnString: clearBtnString,
      queryInputPlaceholder: queryInputPlaceholder,
      errorMessages: errorMessages,
      hintTextString: hintTextString,
      helperHref: this.getHelperLink(),
      manualResize: false,
      enterKeyEnabled: true,
    };

    this.dom.$searchQueryPanel.dateFilterProps = {
      label: datetimeFilterLabel,
      labelWidth: '240',
      min: (new Date().getTime() - this.dependencies.getMinPeriod()) / 1000,
      max: new Date().getTime() / 1000,
      timezone: this.dependencies.getTimezone(),
      timeRangeOptions: [
        {
          label: this.dependencies.translateFn('Last 24 hours'),
          value: 1,
        },
        {
          label: this.dependencies.translateFn('Last 7 days'),
          value: 7,
        },
        {
          label: this.dependencies.translateFn('Last 30 days'),
          value: 30,
        },
      ],
      userLocale: this.dependencies.getUserLanguage(),
    };

    this.dom.$searchQueryPanel.actionBtnProps = {
      saveBtnString: this.commonStrings.save,
      saveAsBtnString: this.commonStrings.saveAs,
      discardBtnString: this.commonStrings.discardChanges,
    };
    this.dom.$searchQueryPanel.companySelector.propertiesSet = false;
    const selectedCompany = this.dependencies.selectedCompany;
    if (selectedCompany) {
      this.dom.$searchQueryPanel.companySelector.selectedTarget = selectedCompany;
    } else {
      this.dom.$searchQueryPanel.companySelector.selectedTarget = this.dependencies.getActiveCompany();
    }
    this.dom.$searchQueryPanel.companySelector.propertiesSet = true;

    const companyId = this.dom.$searchQueryPanel.companySelector.selectedTarget.companyId;
    const demoCompanyId = await ConsoleRequest.createRequest<any>({
      dataProviderType: DataProviderType.CORE,
      endpoint: 'Settings',
      method: 'getXdrDemoCompanyId',
      payload: {},
    });
    this.dom.$searchQueryPanel.disableSmartViewButtonsForDemoCompany(companyId === demoCompanyId);
  }

  public async loadSmartViewItems(selectedItemId: string | null = null) {
    const selectedCompany = this.dependencies.selectedCompany;
    let companyId = this.dependencies.getActiveCompany().companyId;
    if (selectedCompany && !this.dom.$smartViews.started) {
      companyId = selectedCompany.companyId;
    }
    const request = {
      companyId: companyId,
    };

    let response = { success: false };
    try {
      response = await this.dependencies.api.clients.Queries.readRecords(request, getRequestDefaultOptions());
    } catch (err) {
      console.error(err);
    }
    if (!response || !response.success) {
      return;
    }
    const savedQueries = response['data']['saved'];
    const savedItems: any = [];
    for (const value of savedQueries) {
      savedItems.push({
        id: value._id,
        active: value._id === selectedItemId,
        query: value.query,
        viewName: htmlDecode(value.name),
        viewType: ViewType.CUSTOM,
        filters: [],
        displayedColumns: [3],
        selectedTargets: [],
        isCompactView: true,
        pageSize: 50,
        columnConfigs: [
          {
            key: 'dummy',
            width: '450px',
          },
        ],
      });
    }

    const sections: Array<SmartViewSection> = [
      {
        type: SectionType.SAVED_VIEWS,
        sectionTitle: this.translations.savedTextString,
        inlineActions: [
          /*
          // TODO implement rename
          // When implementing rename, ensure we don't add another XSS vulnerability vector.
          // See https://jira.bitdefender.biz/browse/CERES-33211 and related issues & PRs
          {
            title: this.translations.inlineActionsTranslations.rename,
            value: SmartViewInlineActions.RENAME,
          },
        */
          {
            title: this.translations.inlineActionsTranslations.delete,
            value: SmartViewInlineActions.DELETE,
            customClass: 'danger',
          },
        ],
        items: savedItems,
      },
    ];

    if (this.dom.$smartViews.injectDependencies) {
      this.dom.$smartViews.injectDependencies(this.dependencies);
      this.dom.$smartViews.start().catch(console.error);
    }

    this.dom.$smartViews.propertiesSet = false;
    this.dom.$smartViews.controller.view.dom.$sectionsDiv.innerHTML = '';
    this.dom.$smartViews.panelTitle = '';
    this.dom.$smartViews.sections = sections;
    this.dom.$smartViews.propertiesSet = true;
  }

  private getHelperLink(): string {
    if (this.dependencies.isPartnerCompany()) {
      return HELPER_LINKS.partner;
    }
    return HELPER_LINKS.endUser;
  }
}
